import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Buttons from '@components/Buttons/Buttons';
import * as ApiModels from '@/tsClient/servers/noodleDocuments/document-api-models';
import DocumentUploader, { DocumentUploaderRef } from '@components/Document/DocumentUploader';
import { logError } from '@providers/ErrorTracking';
import CircularProgress from '@components/CircularProgress';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import File from '@components/DesignLibrary/File';
import UserProfileContext from '@layouts/UserProfileLayout/UserProfileContext';
import { useOpenDocumentRequestUserFilePane } from '@panes/hooks';
import { useRouter } from 'next/router';
import { getNoodleDocumentsUrl, getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import dashboardContext from '@layouts/DashboardLayout/DashboardContext';
import SecondaryActions from '@components/DesignLibrary/SecondaryActions';
import CollapsibleArea from '@components/CollapsibleArea';
import Upload from '@components/Icons/Upload';
import Lock from '@components/Icons/Lock';
import TrashSimple from '@components/Icons/TrashSimple';
import DotsSixVertical from '@components/Icons/DotsSixVertical';
import BannerStatus from '@components/DesignLibrary/BannerStatus';
import { debounce } from 'lodash';
import DOMPurify from 'isomorphic-dompurify';
import convertLinksToAnchors from '@/helpers/convertLinksToAnchors';
import { DocumentAccessContext } from '@providers/DocumentAccess';
import DocumentAccessModal from '@modals/DocumentAccessModal';
import { Reorder } from 'framer-motion';
import { usePrevious } from '@/hooks';
import ProgressIndicator from '@/components/ProgressIndicator';
import * as format from '@format';
import { useQueryState } from 'nuqs';
import { DOC_ID_QS_KEY } from '@/lib/DocumentRequestUserFileDetail/DocumentRequestUserFileDetail';
import PaystubModal from '@/modals/PaystubModal';

import s from './DocumentFileCard.module.scss';
import CardLabel from './CardLabel';
import ValidationStatusButton from '../DocumentStatus/ValidationStatusButton';
import DocumentSort from '../DocumentSort';

type Props =
  | {
      creatorSlug: string;
      creatorName?: string | null;
      documentRequestUserFileId: string;
      documentRequestUserId: string;
      fileDescription: string | null;
      fileName: string;
      hasPermission: boolean;
      isForYourEyesOnly: boolean;
      isIncomeSource?: boolean;
      isEdit?: false;
      isRequired: boolean;
      onFinishUpload?: (args: {
        document: Parameters<NonNullable<Parameters<typeof DocumentUploader>[0]['onFinishUpload']>>[0];
        documentRequestUserFileId: string;
      }) => Promise<void> | void;
      refetchFiles: () => Promise<void>;
      canDelete: boolean;
      canUpdateIsRequired: boolean;
      documents: Pick<ApiModels.Document, 'id' | 'fileName' | 'createdAt' | 'uploadedBy' | 'mimeType' | 'uploadedAt'>[];
      actionRequired?: Pick<ApiModels.DocumentRequestUserFile, 'rejectedAt' | 'rejectionMessage'>;
      showDocumentValidationStatus?: boolean;
    }
  | {
      creatorSlug?: never;
      creatorName?: never;
      documentId?: never;
      documentRequestUserFileId?: never;
      documentRequestUserId?: never;
      fileDescription: string | null;
      fileName: string;
      hasPermission?: never;
      isForYourEyesOnly?: never;
      isIncomeSource?: never;
      fileSize?: number | null;
      isEdit: true;
      isRequired: boolean;
      onFinishUpload?: never;
      uploadedAt?: string | null;
      uploadedBy?: string | null;
      refetchFiles?: never;
      canDelete?: never;
      canUpdateIsRequired?: never;
      documents?: never;
      actionRequired?: never;
      showDocumentValidationStatus?: boolean;
    };

const DocumentFileCard: FC<Props> = ({
  creatorSlug,
  creatorName,
  documentRequestUserFileId,
  documentRequestUserId,
  fileDescription,
  fileName,
  hasPermission,
  isEdit = false,
  isRequired,
  onFinishUpload,
  isForYourEyesOnly,
  isIncomeSource,
  refetchFiles,
  canDelete,
  canUpdateIsRequired,
  documents,
  actionRequired,
  showDocumentValidationStatus = false,
}) => {
  const router = useRouter();
  const { isInDashboard } = useContext(dashboardContext);
  const { accessToken } = useContext(DocumentAccessContext);
  const { isInUserProfile } = useContext(UserProfileContext);
  const [uploadError, setUploadError] = useState<Error | null>(null);
  const [uploadingProgress, setUploadingProgress] = useState<number>(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingIsRequired, setIsUpdatingIsRequired] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [paystubModalOpen, setPaystubModalOpen] = useState(false);
  const openDocumentRequestUserFilePane = useOpenDocumentRequestUserFilePane();
  const documentUploaderRef = useRef<DocumentUploaderRef>(null);
  const [lastFileUploadedAt, setLastFileUploadedAt] = useState<Date | null>(null);
  const [removingFileId, setRemovingFileId] = useState<string | null>(null);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [files, setFiles] = useState<Array<Parameters<typeof File>[0]['document']>>(documents || []);
  const reorderContainerRef = useRef<HTMLUListElement>(null);
  const [_, setDocumentId] = useQueryState(DOC_ID_QS_KEY);
  const [isSizeError, setIsSizeError] = useState(false);
  const [dragging, setDragging] = useState<undefined | string>('');

  const previousFiles = usePrevious(files);

  const { getData: deleteFn } = useNoodleApi(tsClient.documentRequests.deleteDocumentRequestUserFile);
  const { getData: updateFn } = useNoodleApi(tsClient.documentRequests.updateDocumentRequestUserFile);
  const { getData: removeDocument } = useNoodleApi(tsClient.documentRequests.removeDocumentFromDocumentRequestUserFile);
  const { getData: updateDocumentOrder } = useNoodleApi(tsClient.documentRequests.updateDocumentOrder);

  const {
    data: validationData,
    getData: getDocumentFileValidation,
  } = useNoodleApi(tsClient.documents.getDocumentFileValidationById);
  const [lastRetrievedValidationData, setLastRetrievedValidationData] = useState(validationData);

  useEffect(() => {
    if (!validationData) return;

    setLastRetrievedValidationData(validationData);
  }, [validationData]);

  const validationStatus = useMemo(() => lastRetrievedValidationData?.documentRequestUserFile?.validationStatus, [lastRetrievedValidationData]);
  const documentAnalysis = useMemo(() => lastRetrievedValidationData?.documentAnalysis, [lastRetrievedValidationData]);

  useEffect(() => {
    if (!showDocumentValidationStatus || !documentRequestUserFileId) return;

    getDocumentFileValidation({ fileId: documentRequestUserFileId });
  }, [documentRequestUserFileId, showDocumentValidationStatus, getDocumentFileValidation]);

  const debouncedGetDocumentFileValidation = useMemo(() => debounce(getDocumentFileValidation, 3000, {
    leading: true,
  }), [getDocumentFileValidation]);

  useEffect(() => {
    // Polling for validation status updates at the document request level
    // whenever a file was just uploaded
    if (!lastFileUploadedAt) return;

    const now = new Date();
    const diffInMillis = (now.getTime() - lastFileUploadedAt.getTime());
    const wasJustUploaded = diffInMillis < 15_000; // within last 15s

    if (!wasJustUploaded || !documentRequestUserFileId) return;

    // Polling for when an image was just uploaded
    debouncedGetDocumentFileValidation({ fileId: documentRequestUserFileId });
  }, [
    lastFileUploadedAt,
    documentRequestUserFileId,
    debouncedGetDocumentFileValidation,
    validationData,
  ]);

  useEffect(() => {
    if (files.length > 0 && (previousFiles?.length || 0) > 0 && documentRequestUserId) {
      updateDocumentOrder({
        fileId: documentRequestUserFileId,
        id: documentRequestUserId,
        updates: files.map((f, idx) => ({
          documentId: f.id,
          rank: idx + 1,
        })),
      });
    }
  }, [files, previousFiles, updateDocumentOrder, documentRequestUserFileId, documentRequestUserId]);

  useEffect(() => {
    if (documents) {
      setFiles(documents);
    }
  }, [documents]);

  const handleClick = (): void => {
    if (!isEdit && (documents?.length || 0) === 0) {
      documentUploaderRef.current?.uploadDocument();
    } else if ((documents?.length || 0) > 0 && documentRequestUserFileId && documentRequestUserId && creatorSlug) {
      if (isInDashboard || isInUserProfile) {
        openDocumentRequestUserFilePane({ documentRequestUserFileId });
      } else {
        router.push(
          getUrl(IDENTIFIERS.DOCUMENT_REQUEST_USER_FILE_DETAIL, {
            creatorSlug,
            documentRequestUserFileId,
            documentRequestUserId,
          }),
        );
      }
    } else {
      logError(new Error('Could not go to next step from DocumentFileCard'), {
        creatorSlug,
        documentIds: documents?.map(d => d.id),
        documentRequestUserFileId,
        documentRequestUserId,
      });
    }
  };

  const removeFile = async (documentIdToRemove: string): Promise<void> => {
    if (window.confirm('Are you sure you want to remove this file?') && documentRequestUserId) {
      setRemovingFileId(documentIdToRemove);
      await removeDocument({
        documentId: documentIdToRemove,
        fileId: documentRequestUserFileId,
        id: documentRequestUserId,
      });
      if (refetchFiles) {
        await refetchFiles();
      }
      setRemovingFileId(null);
    }
  };

  const handleDeleteClick = async (): Promise<void> => {
    if (!window.confirm(`Are you sure you want to delete this ${isIncomeSource ? 'income source' : 'document request'}?`)) {
      return;
    }
    if (documentRequestUserFileId && documentRequestUserId) {
      setIsDeleting(true);
      await deleteFn({ fileId: documentRequestUserFileId, id: documentRequestUserId });
      if (refetchFiles) {
        await refetchFiles();
      }
      setIsDeleting(false);
    }
  };

  const downloadFiles = (overrideToken?: string): void => {
    if (!accessToken && !overrideToken) {
      return;
    }
    if (documents) {
      const zipDownloadLink = getNoodleDocumentsUrl('/documents/archive.pdf', {
        accessToken: accessToken || overrideToken,
        archiveName: fileName,
        documentId: documents.map((d) => d.id),
      });
      const a = window.document.createElement('a');
      a.href = zipDownloadLink;
      a.style.display = 'none';
      a.download = `${fileName}.pdf`;
      window.document.body.appendChild(a);
      a.click();
    }
  };

  const handleDownloadFilesAsPDF = async (): Promise<void> => {
    if (!accessToken) {
      setIsAccessModalOpen(true);
    } else {
      downloadFiles();
    }
  };

  const handleUpdateIsRequired = async (): Promise<void> => {
    if (documentRequestUserFileId && documentRequestUserId) {
      setIsUpdatingIsRequired(true);
      await updateFn({ fileId: documentRequestUserFileId, id: documentRequestUserId, isRequired: !isRequired });
      if (refetchFiles) {
        await refetchFiles();
      }
      setIsUpdatingIsRequired(false);
    }
  };

  const handleUploadFinish: NonNullable<Parameters<typeof DocumentUploader>[0]['onFinishUpload']> = async document => {
    setIsUploadingFiles(false);
    if (onFinishUpload && documentRequestUserFileId) {
      await onFinishUpload({ document, documentRequestUserFileId });
    }

    setLastFileUploadedAt(new Date());
  };

  const handleUploadStart = (): void => {
    setIsUploadingFiles(true);
    setUploadError(null);
    setIsSizeError(false);
  };

  const FileComponent: FC<{ document: Pick<ApiModels.Document, 'id' | 'fileName' | 'createdAt' | 'uploadedBy' | 'mimeType' | 'uploadedAt'> }> = ({ document }) => {

    if (!documentRequestUserFileId || !creatorSlug || !documentRequestUserFileId || !documentRequestUserId) return <></>;
    return (
      <button
        key={document.id}
        className={s.file}
        onClick={() => {
          if (isInDashboard || isInUserProfile) {
            openDocumentRequestUserFilePane({ documentRequestUserFileId });
            setDocumentId(document.id);
          } else {
            router.push(
              getUrl(IDENTIFIERS.DOCUMENT_REQUEST_USER_FILE_DETAIL, {
                creatorSlug,
                documentRequestUserFileId,
                documentRequestUserId,
              }),
            );
          }
        }}
      >
        <File document={document} allowRename onRename={async () => {
          await refetchFiles();
        }} />
      </button>
    );
  };

  return (
    <div className={s.wrapper}>
      {documents && documents.length > 0
        && <button
          className={s.trigger}
          onClick={() => openDocumentRequestUserFilePane({ documentRequestUserFileId })}
          aria-label='View uploaded files'
        />}
      <div className={s.card}>
        <>
          {showDocumentValidationStatus
            && <ValidationStatusButton
              status={validationStatus?.status}
              approvedBy={validationStatus?.approvedBy}
              documentAnalysis={{
                documentAnalysis,
                summary: null,
              }}
              onClick={() => {
                if (!documentRequestUserFileId) return;
                openDocumentRequestUserFilePane({ documentRequestUserFileId });
              }}
              fileId={documentRequestUserFileId}
              getDocumentFileValidation={getDocumentFileValidation}
              isRollUp
            />}
          {actionRequired && <BannerStatus status='error' label={`${actionRequired.rejectionMessage}${actionRequired.rejectedAt
            ? ` (${format.datetimeLocalTZ.withoutTimezone({ datetime: actionRequired.rejectedAt })})`
            : ''}`} className={s.banner} />}
          <p className={s.title}>
            {fileName} {!isRequired && <em>If Available</em>} {isRequired && <b>Required</b>}{' '}
          </p>
          {fileDescription && <p className="caption" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(convertLinksToAnchors(fileDescription)) }} />}
          {(documents?.length || 0) === 0 && isForYourEyesOnly && creatorName && (
            <p className="caption">
              <Lock weight="fill" /> This is a sensitive document that will only be visible to the person who uploads it and {creatorName}&apos;s team.
            </p>
          )}
          {documents && <>
            {(documents.length || 0) > 1 && (
              <CollapsibleArea
                title={`${documents.length} uploaded documents`}
                className={s.collapsible}
                caretPosition='left'
              >
                <DocumentSort files={files} setFiles={setFiles} />
                <Reorder.Group ref={reorderContainerRef} as="div" values={files} onReorder={setFiles} axis="y" layoutScroll>
                  {files.map(file => <Reorder.Item
                    value={file}
                    key={file.id}
                    as="div"
                    dragConstraints={reorderContainerRef}
                    whileDrag={{ cursor: 'grabbing', zIndex: 10 }}
                    onDragStart={() => setDragging(file.id)}
                    onDragEnd={() => setDragging(undefined)}
                    className={s.row}
                  >
                    <FileComponent document={file} />
                    <div className={s.controls}>
                      {removingFileId === file.id
                        ? <ProgressIndicator size={20} />
                        : <>
                          <Buttons isWrapper onClick={() => removeFile(file.id)}>
                            <TrashSimple size={16} weight="fill" color="var(--color-error)" />
                          </Buttons>
                          <DotsSixVertical size={16} color="var(--color-gray-50)" style={{ cursor: dragging === file.id ? "grabbing" : "grab" }} />
                        </>}
                    </div>
                  </Reorder.Item>)}
                </Reorder.Group>
              </CollapsibleArea>
            )}
            {(documents.length || 0) === 1 && (
              <div className={s.row}>
                <FileComponent key={documents[0].id} document={documents[0]} />
                <div className={s.controls}>
                  {removingFileId === documents[0].id
                    ? <ProgressIndicator size={20} />
                    : <Buttons isWrapper onClick={() => removeFile(documents[0].id)}>
                      <TrashSimple size={16} weight="fill" color="var(--color-error)" />
                    </Buttons>}
                </div>
              </div>
            )}
          </>}
          {(documents?.length || 0) === 0 && <div className={s.action}>
            <Buttons
              isWrapper
              onClick={isIncomeSource ? () => setPaystubModalOpen(true) : handleClick}
              className={s.uploadButton}
              disabled={!hasPermission && !isEdit}
            >
              <CardLabel
                isUploaded={Boolean((documents?.length || 0) > 0)}
                isEdit={isEdit}
                isUploading={isUploadingFiles}
                hasPermission={hasPermission ?? true}
                isPaystub={isIncomeSource}
                isSizeError={isSizeError}
              />
            </Buttons>
            {isIncomeSource && (
              <p className="caption">
                Please upload each paystub as individual file.
              </p>
            )}
          </div>}
          {uploadError && (
            <p className="caption" style={{ color: 'var(--color-error)' }}>
              Failed to upload document. Please try again.
            </p>
          )}
        </>
        {documentRequestUserFileId && (
          <>
            <DocumentUploader
              ref={documentUploaderRef}
              documentRequestUserFileId={documentRequestUserFileId}
              onUploadProgressChange={setUploadingProgress}
              onUploadError={setUploadError}
              onFinishUpload={handleUploadFinish}
              onUploadStart={handleUploadStart}
              isMultiple
              checkSize={isIncomeSource}
              isSizeError={setIsSizeError}
            />
            {isSizeError && (
              <BannerStatus
                status='error'
                label={`Upload Failed — File Size Exceeds 5 MB Limit`}
              />
            )}
          </>
        )}

      </div>
      {uploadingProgress > 0 && (
        <div className={s.topRight}>
          <CircularProgress progress={uploadingProgress} size={20} />
        </div>
      )}
      {!(uploadingProgress > 0) && (
        <SecondaryActions
          ariaLabel="Document actions"
          position="bottom-left"
          className={s.topRight}
          compact
          options={[
            ...(documents?.length || 0) > 0 && [{
              icon: Upload,
              value: 'Upload more',
            }] || [],
            ...((canUpdateIsRequired && (canDelete || canUpdateIsRequired) && [
              {
                disabled: isUpdatingIsRequired,
                value: isRequired ? 'Make optional' : 'Make required',
              },
            ])
              || []),
            ...((isInDashboard && (canDelete || canUpdateIsRequired) && [
              {
                disabled: false,
                value: 'Download all as a single PDF',
              },
            ]) || []),
            ...((canDelete && [
              {
                destructive: true,
                disabled: isDeleting,
                value: 'Remove',
              },
            ]) || []),
          ]}
          onSelect={e => {
            if (e === 'Remove') {
              handleDeleteClick();
            } else if (e === 'Make optional' || e === 'Make required') {
              handleUpdateIsRequired();
            } else if (e === 'Download all as a single PDF') {
              handleDownloadFilesAsPDF();
            } else if (e === 'Upload more') {
              if (isIncomeSource) {
                setPaystubModalOpen(true);
              } else {
                documentUploaderRef.current?.uploadDocument();
              }
            }
          }}
        />
      )}
      {isAccessModalOpen && (
        <DocumentAccessModal onSubmit={downloadFiles} onClose={() => setIsAccessModalOpen(false)} />
      )}
      {paystubModalOpen && (
        <PaystubModal
          onUpload={() => {
            handleClick();
            setPaystubModalOpen(false);
          }}
          onClose={() => setPaystubModalOpen(false)}
        />
      )}
    </div>
  );
};

export default DocumentFileCard;
